const TYPE_SIZES = {
    char: 1,
    uchar: 1,
    short: 2,
    ushort: 2,
    int: 4,
    uint: 4,
    float: 4,
    double: 8,
};

const SH_C0 = 0.28209479177387814;

function findHeaderEnd(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    const marker = [101, 110, 100, 95, 104, 101, 97, 100, 101, 114]; // end_header

    for (let i = 0; i < bytes.length - marker.length; i++) {
        let match = true;

        for (let j = 0; j < marker.length; j++) {
            if (bytes[i + j] !== marker[j]) {
                match = false;
                break;
            }
        }

        if (match) {
            return i + marker.length + 1;
        }
    }

    throw new Error("PLY header end not found");
}

function parseHeader(headerText) {
    const lines = headerText.split("\n");
    const elements = [];
    let format = null;
    let currentElement = null;

    for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed || trimmed.startsWith("comment")) {
            continue;
        }

        if (trimmed.startsWith("format ")) {
            format = trimmed.split(/\s+/)[1];
            continue;
        }

        if (trimmed.startsWith("element ")) {
            const [, name, count] = trimmed.split(/\s+/);
            currentElement = {
                name,
                count: Number.parseInt(count, 10),
                properties: [],
            };
            elements.push(currentElement);
            continue;
        }

        if (trimmed.startsWith("property ") && currentElement) {
            const parts = trimmed.split(/\s+/);

            if (parts[1] === "list") {
                throw new Error("PLY list properties are not supported");
            }

            const type = parts[1];
            const name = parts[2];

            currentElement.properties.push({ type, name });
        }
    }

    for (const element of elements) {
        let offset = 0;

        for (const property of element.properties) {
            property.offset = offset;
            offset += TYPE_SIZES[property.type];
        }

        element.stride = offset;
    }

    return { format, elements };
}

function readProperty(view, byteOffset, type, littleEndian) {
    switch (type) {
        case "char":
            return view.getInt8(byteOffset);
        case "uchar":
            return view.getUint8(byteOffset);
        case "short":
            return view.getInt16(byteOffset, littleEndian);
        case "ushort":
            return view.getUint16(byteOffset, littleEndian);
        case "int":
            return view.getInt32(byteOffset, littleEndian);
        case "uint":
            return view.getUint32(byteOffset, littleEndian);
        case "float":
            return view.getFloat32(byteOffset, littleEndian);
        case "double":
            return view.getFloat64(byteOffset, littleEndian);
        default:
            throw new Error(`Unsupported PLY property type: ${type}`);
    }
}

function shDcToColor(value) {
    return Math.min(1, Math.max(0, 0.5 + SH_C0 * value));
}

export default function parseGaussianSplatPly(arrayBuffer) {
    const headerEnd = findHeaderEnd(arrayBuffer);
    const headerText = new TextDecoder().decode(
        new Uint8Array(arrayBuffer, 0, headerEnd),
    );
    const { format, elements } = parseHeader(headerText);

    if (format !== "binary_little_endian") {
        throw new Error(`Unsupported PLY format: ${format}`);
    }

    const vertexElement = elements.find((element) => element.name === "vertex");

    if (!vertexElement) {
        throw new Error("PLY file does not contain a vertex element");
    }

    const propertyIndex = Object.fromEntries(
        vertexElement.properties.map((property) => [property.name, property]),
    );

    for (const name of ["x", "y", "z", "f_dc_0", "f_dc_1", "f_dc_2"]) {
        if (!propertyIndex[name]) {
            throw new Error(`PLY vertex element is missing property: ${name}`);
        }
    }

    let dataOffset = headerEnd;
    const vertexCount = vertexElement.count;
    const positions = new Float32Array(vertexCount * 3);
    const colors = new Float32Array(vertexCount * 3);

    for (const element of elements) {
        if (element.name === "vertex") {
            const view = new DataView(arrayBuffer, dataOffset);

            for (let i = 0; i < vertexCount; i++) {
                const base = i * element.stride;
                const i3 = i * 3;

                positions[i3] = readProperty(
                    view,
                    base + propertyIndex.x.offset,
                    "float",
                    true,
                );
                positions[i3 + 1] = -readProperty(
                    view,
                    base + propertyIndex.y.offset,
                    "float",
                    true,
                );
                positions[i3 + 2] = -readProperty(
                    view,
                    base + propertyIndex.z.offset,
                    "float",
                    true,
                );

                colors[i3] = shDcToColor(
                    readProperty(
                        view,
                        base + propertyIndex.f_dc_0.offset,
                        "float",
                        true,
                    ),
                );
                colors[i3 + 1] = shDcToColor(
                    readProperty(
                        view,
                        base + propertyIndex.f_dc_1.offset,
                        "float",
                        true,
                    ),
                );
                colors[i3 + 2] = shDcToColor(
                    readProperty(
                        view,
                        base + propertyIndex.f_dc_2.offset,
                        "float",
                        true,
                    ),
                );
            }
        }

        dataOffset += element.stride * element.count;
    }

    return {
        positions,
        colors,
        count: vertexCount,
    };
}
